import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "cn"

const textLinkVariants = cva(
  "rounded-xs underline-offset-4 outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50",
  {
    variants: {
      variant: {
        primary: "text-primary hover:text-primary/80",
        destructive: "text-destructive hover:text-destructive-700",
        plain: "text-inherit",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  }
)

export type TextLinkProps = React.ComponentProps<"a"> &
  VariantProps<typeof textLinkVariants>

/**
 * Styled hyperlink for semantic links (`<a href="...">` only).
 *
 * Do not use this for clickable actions that are not navigation. Buttons that
 * look like links should render a `<button>` (for example
 * `Button variant="link"`) so they keep correct semantics and keyboard
 * behavior.
 *
 * @example
 * ```tsx
 * <TextLink href="/edit-user/123">Jane Doe</TextLink>
 * <TextLink variant="destructive" href="mailto:ella@grail-talent.com">
 *   ella@grail-talent.com
 * </TextLink>
 * <TextLink variant="plain" href="https://tiktok.com/@user">
 *   @user
 * </TextLink>
 * ```
 */
function TextLink({
  className,
  variant = "primary",
  ...props
}: TextLinkProps) {
  return (
    <a
      data-slot="text-link"
      data-variant={variant}
      className={cn(textLinkVariants({ variant }), className)}
      {...props}
    />
  )
}

export { TextLink, textLinkVariants }
